import { Button, Popup, ScrollView } from "devextreme-react";
import usePopupState from "../hooks/usePopupState";
import { useEffect, useRef, useState } from "react";
import { Position, ToolbarItem } from "devextreme-react/popup";
import EnrollStatusEditor from "./EnrollStatusEditor";
import EnrollStatusViewer from "./EnrollStatusViewer";
import axios from "axios";
import CustomStore from "devextreme/data/custom_store";
import { toastMsg } from "../utils";
import { alert } from "devextreme/ui/dialog";

const EnrollStatusPopup = ({
  contrNo,
  isEditable,
  isEnrollmentStatusUsed,
  yearMonth,
}) => {
  const [enrollStatusDataSource, setEnrollStatusDataSource] = useState(null);
  const { mode, setPopupClose, setPopupEdit, setPopupOpen } = usePopupState();
  const enrollChartRef = useRef(null);
  const enrollMemoRef = useRef(null);
  const nSubjectRef = useRef(null);
  const subjectMemoRef = useRef(null);
  const targetSubjectGridRef = useRef(null);

  useEffect(() => {
    setEnrollStatusDataSource(
      new CustomStore({
        load: async (loadOptions) => {
          const { contrNo } = loadOptions.userData;
          //! api GET R12. Get the enrollment status
          const response = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/enrollStatus/${contrNo}`
          );

          return response.data;
        },
        update: async (contrNo, { target, curr }) => {
          //! api PUT U4. Update the enrollment status
          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/enrollStatus/${contrNo}`,
            {
              target,
              curr,
            },
            { params: { yearMonth } }
          );
          toastMsg(`Enrollment Status가 정상적으로 수정되었습니다`, true);
        },
        errorHandler: (error) => {
          toastMsg(`에러가 발생했습니다. 관리자에게 문의해주세요.`, false);
          console.log(error);
        },
      })
    );
  }, [yearMonth]);

  useEffect(() => {
    if (mode === "view") {
      enrollStatusDataSource.load({ userData: { contrNo } }).then((res) => {
        const { status, descr } = res;
        enrollChartRef.current.instance.option("dataSource", status);
        enrollMemoRef.current.instance.option("dataSource", descr);
      });
    }
  }, [mode]);

  const calculateNSubject = () => {
    //* yearMonth에 해당하는 시험대상자 수가 있다면 해당 시험대상자 수를, 없다면 0을 리턴한다.
    const chartDataSource =
      enrollChartRef.current.instance.option("dataSource");
    return chartDataSource.find((el) => el.yearMonth === yearMonth)?.curr || 0;
  };

  const calculatePrevMonthStackedSubject = () => {
    //* yearMonth에 해당하는 데이터가 있다면 그 yearMonth의 전월 누적 시험대상자 수.
    //* 없다면 이전 중 마지막 누적 시험대상자 수 + 당월 등록 시험대상자 수.
    const chartDataSource =
      enrollChartRef.current.instance.option("dataSource");

    const enrollStatus = chartDataSource.find(
      (element) => element.yearMonth === yearMonth
    );

    if (enrollStatus) {
      return enrollStatus.accum;
    } else {
      return 0;
    }

    // return (
    //   chartDataSource.find((el) => el.yearMonth === yearMonth)?.accum ||
    //   chartDataSource.at(-1)?.curr + chartDataSource.at(-1)?.accum ||
    //   0
    // );
  };

  const calculateSubjectMemo = () => {
    //* yearMonth에 해당하는 memo가 있다면 해당 data를, 없다면 null을 리턴한다.
    const memoDataSource = enrollMemoRef.current.instance.option("dataSource");
    return memoDataSource.find((el) => el.yearMonth === yearMonth)?.descr;
  };

  const calculateTargetDataSource = () => {
    //* chartDataSource에서 targetSubjectGridRef에 들어갈 dataSource를 만든다.
    const chartDataSource =
      enrollChartRef.current.instance.option("dataSource");

    return chartDataSource
      ?.filter(({ yearMonth, target }) => yearMonth && target)
      ?.map(({ yearMonth, target }) => ({ yearMonth, nSubjects: target }));
  };

  const validateGridValue = () => {
    const gridInstance = targetSubjectGridRef.current.instance;

    const validationState =
      gridInstance.getController("validating")._validationState;
    const errorMessages = [];

    const visibleRowKeys = gridInstance.getVisibleRows().map((row) => row.key);

    validationState.forEach((row) => {
      if (visibleRowKeys.includes(row.key) && row.validationResults) {
        const cellValidation = Object.values(row.validationResults);
        cellValidation.forEach((c) => {
          if (c.status === "invalid") {
            const rule = c.brokenRule;
            const message =
              rule.type === "required"
                ? `${c.name}은 필수 항목입니다`
                : rule.message;
            errorMessages.push(message);
          }
        });
      }
    });

    if (errorMessages.length > 0) {
      alert(errorMessages.join("<br />"), "Validation Error");
      return false;
    }
    return true;
  };

  const handleClickSave = async () => {
    if (!validateGridValue()) {
      return;
    }

    const newSubject = nSubjectRef.current.instance.option("value");
    const subjectMemo = subjectMemoRef.current.instance.option("value") || null;

    // 편집 중인 셀을 먼저 확정하고 store에서 전체 row를 읽는다.
    // getVisibleRows()는 virtual scrolling 때문에 로드된 row만 돌려주는데
    // 서버는 받은 row로 전체를 갈아끼우므로, 스크롤하지 않은 row가 지워진다.
    const gridInstance = targetSubjectGridRef.current.instance;
    await gridInstance.saveEditData();

    if (gridInstance.hasEditData()) {
      // DataGrid 자체 validation에 걸린 경우. 해당 셀이 grid에 표시된다.
      return;
    }

    const targetData = await gridInstance.getDataSource().store().load();

    enrollStatusDataSource
      .update(contrNo, {
        target: targetData,
        curr: { yearMonth, nSubjects: newSubject, descr: subjectMemo },
      })
      .then(setPopupOpen)
      // 저장에 실패하면 edit 모드를 유지해 입력값이 사라지지 않게 한다.
      // (에러 토스트는 CustomStore의 errorHandler가 띄운다.)
      .catch(() => {});
  };

  return (
    <>
      <Button
        //! userInfo
        disabled={!isEnrollmentStatusUsed}
        onClick={setPopupOpen}
        stylingMode="contained"
        text="ENROLLMENT STATUS"
        type="success"
      />
      {mode !== "close" && (
        <Popup
          height={1000}
          hideOnOutsideClick={true}
          maxHeight={"90vh"}
          maxWidth={"90vw"}
          onHiding={setPopupClose}
          showCloseButton={true}
          title="Enrollment Status"
          visible={true}
          width={1200}
        >
          <Position at="center" collision="fit" my="center" />
          <ToolbarItem
            location="after"
            options={{
              disabled: !isEditable,
              icon: "edit",
              onClick: setPopupEdit,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={mode !== "edit"}
            widget="dxButton"
          />
          <ToolbarItem
            location="after"
            options={{
              icon: "save",
              onClick: handleClickSave,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={mode === "edit"}
            widget="dxButton"
          />
          <ToolbarItem
            location="after"
            options={{
              icon: "revert",
              onClick: setPopupOpen,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={mode === "edit"}
            widget="dxButton"
          />
          <ScrollView width="100%" height="100%">
            <div>
              {mode === "edit" && (
                <EnrollStatusEditor
                  nSubject={calculateNSubject()}
                  nSubjectRef={nSubjectRef}
                  prevMonthStackedSubject={calculatePrevMonthStackedSubject()}
                  subjectMemo={calculateSubjectMemo()}
                  subjectMemoRef={subjectMemoRef}
                  targetSubjectDataSource={calculateTargetDataSource()}
                  targetSubjectGridRef={targetSubjectGridRef}
                  yearMonth={yearMonth}
                />
              )}
              {mode === "view" && (
                <EnrollStatusViewer
                  chartRef={enrollChartRef}
                  memoRef={enrollMemoRef}
                  contrNo={contrNo}
                />
              )}
            </div>
          </ScrollView>
        </Popup>
      )}
    </>
  );
};

export default EnrollStatusPopup;
